import {
  clearCsrfToken,
  setCsrfToken,
} from "@src/app/api/config/csrf";
import { RegisterConfirmationDto } from "@src/domains/Process/models/Dtos/Core/Auth/RegisterConfirmationDto";
import { CreateUserDto } from "@src/domains/shared/models/Dtos/CreateUserDto";
import { IAuthResult } from "@src/interfaces/IAuthResult";
import { IRegisterResponse } from "@src/interfaces/IRegisterResponse";
import { refreshAuthTokens } from "@src/app/api/config/authRefresh";
import { api } from "../../../app/api/config/api";

const controllerUrl = "/auth";

interface ICsrfTokenResponse {
  csrfToken: string;
}

export const createUser = async (model: CreateUserDto): Promise<void> => {
  try {
    await api.post<IRegisterResponse>(`${controllerUrl}/register`, model);
  } catch (error: unknown) {
    if (error instanceof Error) {
      return Promise.reject(error);
    }
    return Promise.reject(new Error("An unknown error occurred"));
  }
};

export const login = async (
  userName: string,
  password: string,
): Promise<void> => {
  try {
    const response = await api.post<IAuthResult>(`${controllerUrl}/login`, {
      userName,
      password,
    });

    if (!response.data.succeeded) {
      return Promise.reject(new Error("Login failed"));
    }

    await bootstrapCsrfToken();
  } catch (error: unknown) {
    if (error instanceof Error) {
      return Promise.reject(error);
    }
    return Promise.reject(new Error("An unknown error occurred"));
  }
};

export const getRefreshToken = async (): Promise<boolean> => refreshAuthTokens();

export const logoutRequest = async (): Promise<void> => {
  try {
    await api.post(`${controllerUrl}/logout`);
  } catch {
    // Server-side cookie clearing is best-effort during client logout.
  }
};

export const bootstrapCsrfToken = async (): Promise<void> => {
  try {
    const response = await api.get<ICsrfTokenResponse>("/security/csrf-token");
    setCsrfToken(response.data.csrfToken ?? null);
  } catch {
    clearCsrfToken();
  }
};

export const registerConfirmation = async (
  model: RegisterConfirmationDto,
  token: string | null,
): Promise<void> => {
  if (!token) {
    return;
  }

  try {
    await api.post<IRegisterResponse>(
      `${controllerUrl}/register/confirm?token=${token}`,
      model,
    );
  } catch (error: unknown) {
    if (error instanceof Error) {
      return Promise.reject(error);
    }
    return Promise.reject(new Error("An unknown error occurred"));
  }
};

export const getPasswordTokenValidation = async (
  token: string,
): Promise<boolean> => {
  try {
    const response = await api.get<boolean>(
      `${controllerUrl}/register/${token}/validate`,
    );

    return response?.data;
  } catch (error: unknown) {
    if (error instanceof Error) {
      return Promise.reject(error);
    }
    return Promise.reject(new Error("An unknown error occurred"));
  }
};
